import { useState } from 'react';
import {
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  isSameDay,
  isSameMonth,
  differenceInCalendarDays,
} from 'date-fns';
import { DateTimeRange } from '../interface/data-picker/DataPicker';
import { useAppSelector } from './useAppSelector';
import { setRange, validateTimes } from '../store/slice/date-slice';
import { useAppDispatch } from './useAppDispatch';
import {
  DATE_PICKER_CLOSE,
  DATE_PICKER_VISIBLE,
  DIGITS_ONLY_REGEX,
  HOURS_IN_DAY,
  LAST_NINETY_DAYS,
  LAST_SEVEN_DAYS,
  LAST_THIRTY_DAYS,
  MILISECUONDS_IN_MINUTE,
  MINUTES_IN_HOUR_MINUS_ONE,
  MOBILE_BREAKPOINT,
} from '../constant/constants';
import { DateRangeOption, TimeField } from '../enum/DatePicker';
import { useLanguageLocale } from './useLanguageLocale';

export const useDateFilter = (onSearch: (range: DateTimeRange) => void) => {
  const [selectedDateOption, setSelectedDateOption] = useState<DateRangeOption>(DateRangeOption.TODAY);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const range = useAppSelector((state) => state.dateFilter);
  const dispatch = useAppDispatch();
  const { localeString } = useLanguageLocale();

  const togglePicker = () => {
    if (isOpen) {
      setIsVisible(false);
      setTimeout(() => setIsOpen(false), DATE_PICKER_CLOSE);
    } else {
      setIsOpen(true);
      setTimeout(() => setIsVisible(true), DATE_PICKER_VISIBLE);
    }
  };

  const handleDateOptionSelect = (dateOption: DateRangeOption) => {
    const now = new Date();
    let newRange: DateTimeRange = {
      timeFromHour: '00',
      timeToHour: '24',
      timeFromMinute: '00',
      timeToMinute: '00',
    };

    switch (dateOption) {
      case DateRangeOption.TODAY:
        newRange = {
          ...newRange,
          from: now.toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.YESTERDAY:
        const y = subDays(now, 1);
        newRange = { ...newRange, from: y.toISOString(), to: y.toISOString() };
        break;
      case DateRangeOption.LAST_7_DAYS:
        newRange = {
          ...newRange,
          from: subDays(now, LAST_SEVEN_DAYS).toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.LAST_30_DAYS:
        newRange = {
          ...newRange,
          from: subDays(now, LAST_THIRTY_DAYS).toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.LAST_90_DAYS:
        newRange = {
          ...newRange,
          from: subDays(now, LAST_NINETY_DAYS).toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.THIS_MONTH:
        newRange = {
          ...newRange,
          from: startOfMonth(now).toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.LAST_MONTH:
        newRange = {
          ...newRange,
          from: startOfMonth(subMonths(now, 1)).toISOString(),
          to: endOfMonth(subMonths(now, 1)).toISOString(),
        };
        break;
      case DateRangeOption.LAST_12_MONTHS:
        newRange = {
          ...newRange,
          from: subMonths(now, 12).toISOString(),
          to: now.toISOString(),
        };
        break;
      case DateRangeOption.CUSTOM:
        newRange = { ...range };
        break;
    }

    setSelectedDateOption(dateOption);
    dispatch(setRange(newRange));
  };

  const detectDateOption = (from?: Date, to?: Date): DateRangeOption => {
    if (!from || !to) return DateRangeOption.CUSTOM;

    const now = new Date();
    const yesterday = subDays(now, 1);

    if (isSameDay(from, now) && isSameDay(to, now)) return DateRangeOption.TODAY;
    if (isSameDay(from, yesterday) && isSameDay(to, yesterday)) return DateRangeOption.YESTERDAY;
    if (differenceInCalendarDays(to, from) === LAST_SEVEN_DAYS && isSameDay(to, now))
      return DateRangeOption.LAST_7_DAYS;
    if (differenceInCalendarDays(to, from) === LAST_THIRTY_DAYS && isSameDay(to, now))
      return DateRangeOption.LAST_30_DAYS;
    if (differenceInCalendarDays(to, from) === LAST_NINETY_DAYS && isSameDay(to, now))
      return DateRangeOption.LAST_90_DAYS;
    if (isSameMonth(from, now) && isSameDay(from, startOfMonth(now)) && isSameDay(to, now))
      return DateRangeOption.THIS_MONTH;

    const lastMonthStart = startOfMonth(subMonths(now, 1));
    const lastMonthEnd = endOfMonth(subMonths(now, 1));
    if (isSameDay(from, lastMonthStart) && isSameDay(to, lastMonthEnd)) return DateRangeOption.LAST_MONTH;

    const start12 = startOfMonth(subMonths(now, 12));
    if (
      isSameMonth(from, start12) &&
      from.getFullYear() === start12.getFullYear() &&
      isSameMonth(to, now) &&
      to.getFullYear() === now.getFullYear()
    ) {
      return DateRangeOption.LAST_12_MONTHS;
    }

    return DateRangeOption.CUSTOM;
  };

  const handleSelect = (selected: { from?: Date; to?: Date } | undefined) => {
    if (!selected) return;
    const { from, to } = selected;
    dispatch(
      setRange({
        ...range,
        from: from ? from.toISOString() : undefined,
        to: to ? to.toISOString() : undefined,
      }),
    );
    setSelectedDateOption(detectDateOption(from, to));
  };

  const handleTimeChange = (field: TimeField, value: string) => {
    if (!DIGITS_ONLY_REGEX.test(value)) return;

    const num = parseInt(value, 10);
    if (num < 0 || num > HOURS_IN_DAY) return;

    dispatch(setRange({ [field]: value }));
  };

  /**
   * Builds and validates a complete date-time range from the current picker state,
   * formats it to ISO strings, and triggers the search callback with the payload.
   * Ensures the range covers full minutes (00:00:00.000 – 00:59:59.999).
   */
  const handleSearch = () => {
    dispatch(validateTimes());
    if (!range.from || !range.to) return;

    const fromHour = range.timeFromHour?.padStart(2, '0') || '00';
    const toHour = range.timeToHour?.padStart(2, '0') || '24';

    const fromDate = new Date(range.from);
    fromDate.setHours(parseInt(fromHour), 0, 0, 0);

    const toDate = new Date(range.to);
    toDate.setHours(parseInt(toHour), 0, MINUTES_IN_HOUR_MINUS_ONE, MILISECUONDS_IN_MINUTE);

    const payload = {
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
      timeFrom: `${fromHour}:00`,
      timeTo: `${toHour}:00`,
    };

    onSearch(payload);
  };

  const formatDate = (date: Date) => {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const day = date.toLocaleDateString(localeString, { day: '2-digit' });
    let month = date.toLocaleDateString(localeString, { month: 'short' }).replace('.', '').trim();
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
    const yearShort = date.getFullYear().toString().slice(-2);

    return isMobile ? `${day} ${capitalizedMonth} ${yearShort}` : `${day} ${capitalizedMonth} ${date.getFullYear()}`;
  };

  return {
    selectedDateOption,
    isOpen,
    isVisible,
    togglePicker,
    handleDateOptionSelect,
    handleSelect,
    handleTimeChange,
    handleSearch,
    formatDate,
    detectDateOption,
    setSelectedDateOption,
  };
};
